import { PlatformEdition, PLATFORM_FEATURES } from 'src/types/platform-feature';
import type { PlatformFeature, PlatformFeatureMatrix } from 'src/types/platform-feature';
import type { ResourcePath } from 'src/utils/permissions';

import { DEFAULT_PLATFORM_EDITION, EDITION_FEATURE_MATRIX } from './edition-matrix';
import { PLATFORM_FEATURE_RESOURCES } from './features';

/** What the current platform's edition allows — the single runtime answer. */
export type PlatformFeatureAccess = {
  edition: PlatformEdition;
  isFeatureEnabled: (feature: PlatformFeature) => boolean;
  /** False when the resource belongs to a feature the edition does not have. */
  isResourceAvailable: (path: ResourcePath) => boolean;
};

type PlatformEditionSource = {
  edition?: PlatformEdition;
  feature_matrix?: PlatformFeatureMatrix;
};

const isPlatformEdition = (value: unknown): value is PlatformEdition =>
  Object.values(PlatformEdition).includes(value as PlatformEdition);

export const resolvePlatformFeatures = (
  platform: PlatformEditionSource | null | undefined
): PlatformFeatureAccess => {
  // An edition the backend ships before the frontend knows it falls back to the default.
  const sentEdition = isPlatformEdition(platform?.edition) ? platform.edition : null;
  const edition = sentEdition ?? DEFAULT_PLATFORM_EDITION;
  const editionDefaults = EDITION_FEATURE_MATRIX[edition];
  const overrides = platform?.feature_matrix ?? {};

  // Only known features are read, so keys the backend adds before the frontend are ignored.
  const disabled = new Set<PlatformFeature>(
    Object.values(PLATFORM_FEATURES).filter(
      (feature) => !(overrides[feature] ?? editionDefaults[feature])
    )
  );

  const unavailableResources = new Set<string>(
    Array.from(disabled).flatMap((feature) => PLATFORM_FEATURE_RESOURCES[feature])
  );

  return {
    edition,
    isFeatureEnabled: (feature) => !disabled.has(feature),
    isResourceAvailable: (path) => !unavailableResources.has(path),
  };
};
